/**
 * Frontend Module Registry
 *
 * Each toggleable product module is described here. The `enabled` flag is
 * sourced from `env.ts` (VITE_MODULE_* vars that mirror backend env toggles).
 *
 * The router builds its route tree by iterating enabled modules only.
 * The nav renders only enabled modules' entries.
 * A disabled module contributes NO routes/nav and must NOT break the build.
 *
 * Note on lazyComponent: factories are called only at navigation time and only
 * when the module is enabled. No module flags are enabled by default (they
 * require explicit VITE_MODULE_* env vars), so the factories are never invoked
 * until the respective page modules are implemented by later threads.
 */

import type { ComponentType } from 'react';
import {
  MODULE_PING,
  MODULE_LIFTS,
  MODULE_ORGANIZATIONS,
  MODULE_RBAC,
  MODULE_INVITATIONS,
  MODULE_MFA,
  MODULE_AUDIT,
  MODULE_BILLING,
  MODULE_DOCUMENTS,
} from './env';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single navigation entry for a module. */
export interface ModuleNavEntry {
  /** i18n key for the label (resolved by the nav component via t()). */
  labelKey: string;
  /** Route path (must match a registered route). */
  path: string;
  /** Optional icon identifier (resolved by the nav component). */
  icon?: string;
  /** Relative ordering within the nav. Lower = higher up. */
  order?: number;
}

/** A route descriptor. The actual route object is built by the router. */
export interface ModuleRouteDescriptor {
  /** The URL path segment (e.g. "/organizations"). */
  path: string;
  /**
   * Lazy import factory for the page component.
   * The router calls this only at navigation time (code-split).
   * The factory must resolve to an object with a `default` React component.
   */
  lazyComponent: () => Promise<{ default: ComponentType }>;
}

/** Full descriptor for a product module. */
export interface ModuleDescriptor {
  /** Unique machine-readable identifier (kebab-case). */
  id: string;
  /** Whether this module is active. Sourced from env. */
  enabled: boolean;
  /** Routes contributed by this module. Empty when disabled. */
  routes: ModuleRouteDescriptor[];
  /** Nav entries contributed by this module. Empty when disabled. */
  nav: ModuleNavEntry[];
}

// ---------------------------------------------------------------------------
// Internal helper
// ---------------------------------------------------------------------------

/**
 * Lazy-import factories for every page module, keyed by source path.
 *
 * ``import.meta.glob`` is what makes module-registered routes work in a
 * production build.  The previous approach used ``import(/* @vite-ignore *\/ path)``
 * with a raw ``/src/...`` string, which Vite deliberately does NOT bundle — so at
 * runtime the browser tried to fetch ``/src/pages/lifts/index.ts`` verbatim.  The
 * dev server serves ``/src/...``, so it worked locally; a production build only
 * emits hashed ``/assets/*.js``, so every such route 404'd with
 * "Failed to fetch dynamically imported module".
 *
 * glob, by contrast, code-splits each matched module into its own chunk and
 * resolves the string key to the correct hashed URL at runtime.  Keys are
 * project-root-relative paths (``/`` == the ``frontend/`` root), which is exactly
 * the string form the registry already passes to ``deferredPage`` below.
 *
 * Test files are excluded so they never end up in the production bundle.
 */
const PAGE_MODULES = import.meta.glob<{ default: ComponentType }>([
  '/src/pages/**/index.ts',
  '/src/pages/**/index.tsx',
  '/src/pages/**/*Page.tsx',
  '!/src/pages/**/*.{test,spec}.{ts,tsx}',
  '!/src/pages/**/__tests__/**',
]);

/**
 * Resolve a page module's lazy loader by its source path.
 *
 * The factory is only ever called at navigation time, and only for enabled
 * modules.  A missing key (path typo, or a module not present in this build)
 * fails loudly when navigated to rather than silently rendering nothing.
 */
function deferredPage(modulePath: string): () => Promise<{ default: ComponentType }> {
  const loader = PAGE_MODULES[modulePath];
  if (!loader) {
    return () =>
      Promise.reject(new Error(`Page module not found in bundle: ${modulePath}`));
  }
  return loader;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

/**
 * The authoritative list of all known modules.
 *
 * When a module is disabled its `routes` and `nav` arrays are intentionally
 * empty so the router/nav simply has nothing to register — no runtime checks
 * required at the call sites.
 */
export const MODULE_REGISTRY: ModuleDescriptor[] = [
  // ---------------------------------------------------------------------------
  // documents — Unterlagen (document management) module.
  // Provides the document detail page (/unterlagen/$id) and the all-documents
  // list page (/unterlagen). The dashboard widget and lift-detail section are
  // gated inside those pages using isDocumentsModuleEnabled(). On by default;
  // VITE_MODULE_DOCUMENTS=false to disable.
  // ---------------------------------------------------------------------------
  {
    id: 'documents',
    enabled: MODULE_DOCUMENTS,
    routes: MODULE_DOCUMENTS
      ? [
          {
            path: '/unterlagen',
            lazyComponent: deferredPage('/src/pages/documents-list/index.ts'),
          },
          {
            path: '/unterlagen/$id',
            lazyComponent: deferredPage('/src/pages/document-detail/index.ts'),
          },
        ]
      : [],
    nav: MODULE_DOCUMENTS
      ? [{ labelKey: 'nav.documents', path: '/unterlagen', icon: 'file-text', order: 2 }]
      : [],
  },
  // ---------------------------------------------------------------------------
  // lifts — core customer portal feature (always-on by default; VITE_MODULE_LIFTS=false to hide)
  // Displays the authenticated customer's lifts and links to the lift detail shell.
  // ---------------------------------------------------------------------------
  {
    id: 'lifts',
    enabled: MODULE_LIFTS,
    routes: MODULE_LIFTS
      ? [
          {
            path: '/lifts',
            lazyComponent: deferredPage('/src/pages/lifts/index.ts'),
          },
          {
            path: '/lifts/$id',
            lazyComponent: deferredPage('/src/pages/lift-detail/index.ts'),
          },
        ]
      : [],
    nav: MODULE_LIFTS
      ? [{ labelKey: 'nav.lifts', path: '/lifts', icon: 'building-2', order: 1 }]
      : [],
  },
  // ---------------------------------------------------------------------------
  // ping — system health-check page (always-on by default; VITE_MODULE_PING=false to hide)
  // This is the worked example slice that exercises every FSD layer end-to-end.
  // ---------------------------------------------------------------------------
  {
    id: 'ping',
    enabled: MODULE_PING,
    routes: MODULE_PING
      ? [
          {
            path: '/ping',
            lazyComponent: deferredPage('/src/pages/ping/index.ts'),
          },
        ]
      : [],
    nav: MODULE_PING
      ? [{ labelKey: 'nav.ping', path: '/ping', icon: 'activity', order: 0 }]
      : [],
  },
  {
    id: 'organizations',
    enabled: MODULE_ORGANIZATIONS,
    routes: MODULE_ORGANIZATIONS
      ? [
          {
            path: '/organization/settings',
            lazyComponent: deferredPage('/src/pages/organization/settings/OrganizationSettingsPage.tsx'),
          },
        ]
      : [],
    nav: MODULE_ORGANIZATIONS
      ? [{ labelKey: 'nav.organizations', path: '/organization/settings', icon: 'building', order: 10 }]
      : [],
  },
  {
    // RBAC has no standalone page — member/role management is surfaced inside the
    // organization settings page (permission-gated). The module flag enables
    // those controls; it contributes no separate route/nav entry.
    id: 'rbac',
    enabled: MODULE_RBAC,
    routes: [],
    nav: [],
  },
  {
    // Invitations has no standalone page/nav — the invite form + pending-invite
    // rows are surfaced inside the organization settings page (widgets/
    // members-management), permission-gated. The module flag enables that UI;
    // it contributes no separate route/nav entry (mirrors the RBAC module).
    id: 'invitations',
    enabled: MODULE_INVITATIONS,
    routes: [],
    nav: [],
  },
  {
    // MFA UI lives in the CORE security-settings page (/settings/security tab),
    // which gates its MFA section on this module flag. The module contributes no
    // route/nav of its own — Settings (incl. the Security tab) is always reachable
    // via the core 'settings' nav entry below and the user menu.
    id: 'mfa',
    enabled: MODULE_MFA,
    routes: [],
    nav: [],
  },
  {
    // Settings is core (always available): Profile / Security / Preferences are
    // tabs in one centralised view. The nav points at the first tab; the three
    // /settings/* routes are wired directly in app/router.tsx (not the registry).
    id: 'settings',
    enabled: true,
    routes: [],
    nav: [{ labelKey: 'nav.settings', path: '/settings/profile', icon: 'settings', order: 40 }],
  },
  {
    id: 'audit',
    enabled: MODULE_AUDIT,
    routes: MODULE_AUDIT
      ? [{ path: '/audit', lazyComponent: deferredPage('/src/pages/audit/index.ts') }]
      : [],
    nav: MODULE_AUDIT
      ? [{ labelKey: 'nav.audit', path: '/audit', icon: 'clipboard', order: 50 }]
      : [],
  },
  {
    id: 'billing',
    enabled: MODULE_BILLING,
    routes: MODULE_BILLING
      ? [{ path: '/billing', lazyComponent: deferredPage('/src/pages/billing/index.tsx') }]
      : [],
    nav: MODULE_BILLING
      ? [{ labelKey: 'nav.billing', path: '/billing', icon: 'credit-card', order: 60 }]
      : [],
  },
];

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/**
 * Returns whether the organizations module is enabled.
 * Used by widgets/org-switcher and other org-aware slices to guard rendering.
 * Prefer this over reading MODULE_ORGANIZATIONS directly from env.ts outside of modules.ts.
 */
export function isOrganizationsModuleEnabled(): boolean {
  return MODULE_ORGANIZATIONS;
}

/**
 * Returns whether the documents module is enabled.
 * Used by widgets/latest-documents, pages/lift-detail, and pages/lifts
 * to gate document UI when the module is off.
 */
export function isDocumentsModuleEnabled(): boolean {
  return MODULE_DOCUMENTS;
}

/**
 * Returns whether the invitations module is enabled.
 * Used by features/widgets that should surface invitation UI only when the module is on.
 */
export function isInvitationsModuleEnabled(): boolean {
  return MODULE_INVITATIONS;
}

/**
 * Returns whether the MFA module is enabled.
 * Used by the security settings page and login flow to gate MFA UI.
 */
export function isMfaModuleEnabled(): boolean {
  return MODULE_MFA;
}

/** Returns only modules whose `enabled` flag is true. */
export function getEnabledModules(): ModuleDescriptor[] {
  return MODULE_REGISTRY.filter((m) => m.enabled);
}

/** Returns all nav entries across enabled modules, sorted by `order`. */
export function getEnabledNav(): ModuleNavEntry[] {
  return getEnabledModules()
    .flatMap((m) => m.nav)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Returns all route descriptors across enabled modules. */
export function getEnabledRoutes(): ModuleRouteDescriptor[] {
  return getEnabledModules().flatMap((m) => m.routes);
}
